import { randomBytes } from 'node:crypto';

import type { HttpClient } from '../core/http.js';
import type { Page, RequestOptions } from '../types/common.js';
import type {
  CreateWebhookSettingParams,
  ListWebhookSettingsParams,
  UpdateWebhookSettingParams,
  WebhookSetting,
} from '../types/webhook.js';

export interface CreateHmacEndpointParams {
  /** Where GPM Pay should POST transaction events. */
  url: string;
  name?: string;
  scope?: 'ALL' | 'SPECIFIC';
  bankAccountIds?: string[];
  /** Provide your own, or let the SDK generate a 256-bit hex secret. */
  secret?: string;
}

export interface CreateHmacEndpointResult {
  setting: WebhookSetting;
  /**
   * The signing secret, in plaintext.
   *
   * @remarks
   * Returned **once**. The API stores it encrypted and never gives it back —
   * persist it now, e.g. as `GPMPAY_WEBHOOK_SECRET`.
   */
  secret: string;
}

/** Manage webhook endpoints. Scope: `webhooks:manage`. */
export class WebhookSettingsResource {
  constructor(private readonly http: HttpClient) {}

  list(
    params: ListWebhookSettingsParams = {},
    options: RequestOptions = {},
  ): Promise<Page<WebhookSetting>> {
    return this.http.requestList<WebhookSetting>('GET', '/webhook-settings', {
      ...options,
      query: params as Record<string, unknown>,
    });
  }

  retrieve(id: string, options: RequestOptions = {}): Promise<WebhookSetting> {
    return this.http.request<WebhookSetting>(
      'GET',
      `/webhook-settings/${encodeURIComponent(id)}`,
      options,
    );
  }

  create(
    params: CreateWebhookSettingParams,
    options: RequestOptions = {},
  ): Promise<WebhookSetting> {
    return this.http.request<WebhookSetting>('POST', '/webhook-settings', {
      ...options,
      body: params,
    });
  }

  update(
    id: string,
    params: UpdateWebhookSettingParams,
    options: RequestOptions = {},
  ): Promise<WebhookSetting> {
    return this.http.request<WebhookSetting>(
      'PATCH',
      `/webhook-settings/${encodeURIComponent(id)}`,
      { ...options, body: params },
    );
  }

  remove(id: string, options: RequestOptions = {}): Promise<unknown> {
    return this.http.request<unknown>(
      'DELETE',
      `/webhook-settings/${encodeURIComponent(id)}`,
      options,
    );
  }

  /**
   * Register an HTTP endpoint with HMAC signing and hand back the secret.
   *
   * Pair the returned secret with `verifyWebhookSignature` from
   * `@gpmpay/sdk/webhooks`.
   */
  async createHmacEndpoint(
    params: CreateHmacEndpointParams,
    options: RequestOptions = {},
  ): Promise<CreateHmacEndpointResult> {
    const secret = params.secret ?? randomBytes(32).toString('hex');

    const base = {
      driver: 'HTTP' as const,
      url: params.url,
      authorizationType: 'HMAC' as const,
      authorizationSecret: secret,
      ...(params.name === undefined ? {} : { name: params.name }),
    };

    const body: CreateWebhookSettingParams =
      params.scope === 'SPECIFIC'
        ? {
            ...base,
            scope: 'SPECIFIC',
            bankAccountIds: (params.bankAccountIds ?? []) as [
              string,
              ...string[],
            ],
          }
        : { ...base, scope: 'ALL' };

    const setting = await this.create(body, options);
    return { setting, secret };
  }
}
